/**
 * useSalesManagerData Hook
 *
 * Custom hook for fetching and managing sales manager performance data.
 * Includes loading/error state management and sorting functionality.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getSalesManagerPerformance,
  refreshSalesManagerData,
  SalesManagerPerformanceResponse,
} from '../services/api';
import {
  SalesManager,
  SalesManagerSummary,
  SortConfig,
  SortField,
} from '../components/forecast/SalesManagerPerformance/types';

interface UseSalesManagerDataParams {
  locationId: number;
  year: number;
  month: number;
  enabled: boolean; // Only fetch when NEW_VEHICLE department selected
}

interface UseSalesManagerDataResult {
  managers: SalesManager[];
  summary: SalesManagerSummary | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  sortConfig: SortConfig;
  setSortConfig: (config: SortConfig) => void;
  refresh: () => Promise<void>;
}

/**
 * Transform raw API response to frontend SalesManager type
 */
function transformManager(raw: SalesManagerPerformanceResponse['managers'][0]): SalesManager {
  return {
    managerId: raw.managerId,
    managerName: raw.managerName,
    locationId: raw.locationId,
    year: raw.year,
    month: raw.month,
    totalUnits: raw.totalUnits,
    newUnits: raw.newUnits,
    usedUnits: raw.usedUnits,
    swapUnits: raw.swapUnits ?? 0,
    wholesaleUnits: raw.wholesaleUnits ?? 0,
    frontGross: raw.frontGross,
    fiGross: raw.fiGross,
    holdback: raw.holdback,
    totalGross: raw.totalGross,
    avgFrontGrossPerUnit: raw.avgFrontGrossPerUnit,
    avgFiGrossPerUnit: raw.avgFiGrossPerUnit,
    avgHoldbackPerUnit: raw.avgHoldbackPerUnit,
    avgTotalGrossPerUnit: raw.avgTotalGrossPerUnit,
  };
}

/**
 * Transform API summary to frontend type
 */
function transformSummary(
  raw: SalesManagerPerformanceResponse['summary'],
  locationId: number,
  year: number,
  month: number
): SalesManagerSummary | null {
  if (!raw) return null;

  return {
    locationId,
    year,
    month,
    managerCount: raw.managerCount,
    totalGross: raw.totalGross,
    totalFrontGross: raw.totalFrontGross,
    totalFiGross: raw.totalFiGross,
    totalHoldback: raw.totalHoldback,
    totalUnits: raw.totalUnits,
    totalNewUnits: raw.totalNewUnits,
    totalUsedUnits: raw.totalUsedUnits,
    totalSwapUnits: raw.totalSwapUnits ?? 0,
    totalWholesaleUnits: raw.totalWholesaleUnits ?? 0,
    lastUpdated: raw.lastUpdated,
  };
}

export function useSalesManagerData({
  locationId,
  year,
  month,
  enabled,
}: UseSalesManagerDataParams): UseSalesManagerDataResult {
  const [managers, setManagers] = useState<SalesManager[]>([]);
  const [summary, setSummary] = useState<SalesManagerSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'totalGross',
    direction: 'desc',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getSalesManagerPerformance(locationId, year, month);

      if (response.success && response.data) {
        const transformed = response.data.managers.map(transformManager);
        setManagers(transformed);
        setSummary(transformSummary(response.data.summary, locationId, year, month));
      } else {
        setError(response.error || 'Failed to load sales manager data');
        setManagers([]);
        setSummary(null);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setManagers([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [locationId, year, month]);

  // Fetch data when enabled and params change
  useEffect(() => {
    if (!enabled || locationId === 0) {
      setManagers([]);
      setSummary(null);
      setError(null);
      return;
    }

    fetchData();
  }, [locationId, year, month, enabled, fetchData]);

  const refresh = useCallback(async () => {
    if (!enabled || locationId === 0) return;

    setRefreshing(true);
    setError(null);

    try {
      const refreshResponse = await refreshSalesManagerData(locationId, year, month);
      if (!refreshResponse.success) {
        setError(refreshResponse.error || 'Failed to refresh sales manager data');
        return;
      }

      // Re-fetch the updated data from DynamoDB
      const response = await getSalesManagerPerformance(locationId, year, month);
      if (response.success && response.data) {
        const transformed = response.data.managers.map(transformManager);
        setManagers(transformed);
        setSummary(transformSummary(response.data.summary, locationId, year, month));
      } else {
        setError(response.error || 'Failed to load updated sales manager data');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred during refresh');
    } finally {
      setRefreshing(false);
    }
  }, [locationId, year, month, enabled]);

  // Sort managers based on sortConfig
  const sortedManagers = useMemo(() => {
    if (!managers.length) return [];

    return [...managers].sort((a, b) => {
      const { field, direction } = sortConfig;
      let comparison = 0;

      if (field === 'name') {
        comparison = a.managerName.localeCompare(b.managerName);
      } else {
        const aVal = a[field] ?? 0;
        const bVal = b[field] ?? 0;
        comparison = aVal - bVal;
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  }, [managers, sortConfig]);

  const handleSortChange = useCallback((config: SortConfig) => {
    setSortConfig(config);
  }, []);

  return {
    managers: sortedManagers,
    summary,
    loading,
    refreshing,
    error,
    sortConfig,
    setSortConfig: handleSortChange,
    refresh,
  };
}
